/**
 * @file CustomerActivityFeedSection.tsx
 * @description Единая лента активности клиента (phase10, F9).
 *
 * Использует `GET /admin/customers/:id/timeline` — все события клиента в
 * одной хронологии (CRM-активности + Order-events + Notifications).
 *
 * phase19.31 (SC63): блок не перестраивается — его заменит общий компонент
 * ленты следующим шагом. Поправлено только: заголовок «Лента», у блока нет
 * своей прокрутки (прокручивается страница), шрифт не мельче 12px.
 */

import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert } from "../../../../components/ui/alert";
import { Badge } from "../../../../components/ui/badge";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import { ApiError } from "../../../../lib/api-client";
import {
  getCustomerTimeline,
  type TimelineActivityKind,
  type TimelineItem,
} from "../../../../lib/api-orders";

const KIND_LABEL: Record<TimelineActivityKind, string> = {
  NOTE: "Заметка",
  TASK: "Задача",
  CALL: "Звонок",
  EMAIL: "Email",
  SMS: "SMS",
  MEETING: "Встреча",
  ORDER_CREATED: "Заказ создан",
  ORDER_CONFIRMED: "Заказ подтверждён",
  PAYMENT_RECEIVED: "Оплата получена",
  SHIPMENT_SENT: "Отгружено",
  SHIPMENT_DELIVERED: "Доставлено",
  ORDER_COMPLETED: "Заказ завершён",
  ORDER_CANCELLED: "Заказ отменён",
  RETURN_REQUESTED: "Возврат запрошен",
  RETURN_COMPLETED: "Возврат завершён",
  NOTIFICATION_SENT: "Уведомление",
  FISCAL_RECEIPT_PUNCHED: "Чек ФЗ-54",
};

const KIND_TONE: Record<TimelineActivityKind, "default" | "secondary" | "destructive" | "outline"> = {
  NOTE: "outline",
  TASK: "outline",
  CALL: "outline",
  EMAIL: "outline",
  SMS: "outline",
  MEETING: "outline",
  ORDER_CREATED: "secondary",
  ORDER_CONFIRMED: "secondary",
  PAYMENT_RECEIVED: "default",
  SHIPMENT_SENT: "default",
  SHIPMENT_DELIVERED: "default",
  ORDER_COMPLETED: "default",
  ORDER_CANCELLED: "destructive",
  RETURN_REQUESTED: "destructive",
  RETURN_COMPLETED: "secondary",
  NOTIFICATION_SENT: "outline",
  FISCAL_RECEIPT_PUNCHED: "secondary",
};

interface Props {
  customerId: string;
}

export function CustomerActivityFeedSection({ customerId }: Props) {
  const [items, setItems] = useState<TimelineItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await getCustomerTimeline(customerId, { limit: 50 });
        setItems(res.items);
        setTotal(res.total);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
      }
    }
    void load();
  }, [customerId]);

  return (
    <EditorialCard>
      <EditorialCard.Header
        title="Лента"
        subtitle={
          items === null
            ? undefined
            : `Всего ${total} событий` + (items.length < total ? ` (показаны последние ${items.length})` : "")
        }
      />
      <EditorialCard.Body className="p-0">
        {error ? <Alert severity="error">{error}</Alert> : null}
        {items === null ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-6 animate-spin text-on-surface-variant" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-on-surface-variant">
            Событий пока не зафиксировано.
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-on-surface/8">
            {items.map((a) => (
              <li key={a.id} className="px-4 py-3 hover:bg-surface/40">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={KIND_TONE[a.kind]}>
                        {KIND_LABEL[a.kind]}
                      </Badge>
                      <span className="text-sm font-medium break-words">
                        {a.title}
                      </span>
                    </div>
                    {a.content ? (
                      <p className="text-xs text-on-surface-variant mt-1 whitespace-pre-wrap break-words">
                        {a.content}
                      </p>
                    ) : null}
                    <p className="type-caption text-on-surface-variant mt-2">
                      {a.createdBy?.displayName ?? "—"} ·{" "}
                      {new Date(a.createdAt).toLocaleString("ru-RU")}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
